import {store} from '../../redux/store';
import {OpenCountryPickerProps} from '../../interfaces/connected-components/countryPickerModal';
import {hideCountryPickerModal, showCountryPickerModal} from './reducer';
import {Country} from '../../interfaces/api/auth/login';

// Cache for previously filtered country sets to improve performance
const filteredCountriesCache: Record<string, Country[]> = {};

/**
 * This function opens the Country picker modal
 */
export const openCountryPickerModal = ({
  onSelectionChange,
  currentValue = 'en',
  filteredCountryIds,
  withSearch = true,
}: OpenCountryPickerProps) => {
  store.dispatch(
    showCountryPickerModal({
      onSelectionChange,
      currentValue,
      filteredCountryIds,
      withSearch,
    }),
  );
};

/**
 * This function closes the Country picker modal
 */
export const closeCountryPickerModal = () => {
  store.dispatch(hideCountryPickerModal());
};

/**
 * Search and filter countries based on name and/or IDs
 *
 * Optimized with caching for better performance when
 * searching the same terms multiple times
 *
 * @param searchName - Optional search term to filter by name
 * @param countryIds - Optional country IDs to filter by
 * @param tempCountries - Optional temporary country list
 * @returns Filtered list of countries
 */
export const searchAndFilterCountries = (
  searchName: string = '',
  countryIds: string[] | null = null,
  tempCountries: Country[] | null = null,
): Country[] => {
  try {
    const lowerCaseSearchName = searchName.toLowerCase().trim();
    const {countries} = store.getState()?.auth?.config || {};

    // Safety check - ensure we have countries
    if (!countries && !tempCountries) {
      return [];
    }

    // Skip cache for initial load to ensure fresh data
    const skipCache = searchName === '' && !countryIds && !tempCountries;

    // Create a cache key based on search parameters
    const cacheKey = skipCache
      ? null
      : `${lowerCaseSearchName}:${countryIds?.join(',') || ''}:${
          tempCountries ? 'temp' : 'store'
        }`;

    // Return cached results if available and not skipping cache
    if (cacheKey && filteredCountriesCache[cacheKey]) {
      return filteredCountriesCache[cacheKey];
    }

    // Use tempCountries if provided, otherwise fall back to the store countries
    const countriesArray = (
      tempCountries ? tempCountries : countries || []
    ) as Country[];

    // Early return for common case - no filters applied
    if (!lowerCaseSearchName && (!countryIds || countryIds.length === 0)) {
      if (cacheKey) {
        filteredCountriesCache[cacheKey] = countriesArray;
      }
      return countriesArray;
    }

    let filteredCountries = countriesArray;

    // Filter by country IDs if provided and tempCountries is not used
    if (!tempCountries && countryIds && countryIds.length > 0) {
      // Create a set for faster lookup
      const countryIdSet = new Set(countryIds.map(id => String(id)));

      // Filter using set lookup instead of array.find for better performance
      filteredCountries = (countries || []).filter(country =>
        countryIdSet.has(String(country.id)),
      );
    }

    // Further filter by searchName if provided
    if (lowerCaseSearchName) {
      filteredCountries = filteredCountries.filter(country =>
        country.name.toLowerCase().includes(lowerCaseSearchName),
      );
    }

    // Only cache when we have a valid key
    if (cacheKey) {
      // Limit cache size to prevent memory issues
      if (Object.keys(filteredCountriesCache).length > 100) {
        // Clear cache if it gets too large
        Object.keys(filteredCountriesCache)
          .slice(0, 50)
          .forEach(key => {
            delete filteredCountriesCache[key];
          });
      }

      filteredCountriesCache[cacheKey] = filteredCountries;
    }

    return filteredCountries;
  } catch (error) {
    return [];
  }
};
